import React, { useContext, useEffect, useState, useRef, useMemo } from 'react';

import { ArrowUturnLeftIcon, CheckCircleIcon, PencilIcon, PlusIcon, TrashIcon, XCircleIcon, IdentificationIcon } from '@heroicons/react/24/outline';
import Swal from 'sweetalert2';

import { AuthContext } from "../contexts/auth";
import { addEmployeeContract, getCCNLList, getCCNLListPaginated, getContractTypesListPaginated, getEmployeeContractsList, updateEmployeeContract, deleteEmployeeContract } from '../services/api_contracts_employee';
import { getEmployeeWeeklyWorkingHour } from '../services/api_working_hours';
import { getWorkTaskListPaginated, getEntePrevidenzialeList, getEntePrevidenzialeListPaginated, getMandatoryHiringTypesList, getClassificationLevelListPaginated, getWorkingHourTypesListPaginated } from '../services/api_resources';
import { getJustificationProfiles, updateJustificationProfile } from '../services/api_justification_profiles';
import ListBox from './list_box';
import { estraiMessaggiErrore } from '../services/utils';
import { buildDefaultEmailCsv  } from '../services/utils_email';
import { getContractRecipientsDefault } from '../services/api';
import RecipientsField from './recipients_field';
import Fetching from './fetching';
import AsyncSingleSelectSearchbar from './async_single_select_searchbar';
import JustificationProfileListBox from './justification_profile_listbox';
import { v2EstraiMessaggiErrore } from '../services/utils';


const clampTiming = (n) => {
  if (n == null) return null;
  let x = parseInt(String(n), 10);
  if (Number.isNaN(x)) return null;
  if (x < 1) x = 1;
  if (x > 31) x = 31;
  return x;
};

const splitEmails = (csv) =>
  String(csv || "")
    .split(/[,;\s]+/)
    .map((t) => t.trim())
    .filter(Boolean);

const normalizeEmailCsv = (csv) => {
  const uniq = Array.from(new Set(splitEmails(csv).map((e) => e.toLowerCase())));
  return uniq.join(", ");
};

const sameEmailSet = (aCsv, bCsv) => {
  const a = new Set(splitEmails(normalizeEmailCsv(aCsv)).map((e) => e.toLowerCase()));
  const b = new Set(splitEmails(normalizeEmailCsv(bCsv)).map((e) => e.toLowerCase()));
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
};

const buildEffectiveNotifications = ({ timingInput, emailInput, defaultTimingStr, defaultEmailCsv }) => {
  const effectiveTiming = clampTiming(
    String(timingInput || "").trim() === "" ? defaultTimingStr : timingInput
  );

  const effectiveEmailCsv = normalizeEmailCsv(
    String(emailInput || "").trim() === "" ? defaultEmailCsv : emailInput
  );

  const defaultTiming = clampTiming(defaultTimingStr);
  const defaultEmail = normalizeEmailCsv(defaultEmailCsv);

  const matchesDefault =
    effectiveTiming === defaultTiming && sameEmailSet(effectiveEmailCsv, defaultEmail);

  return { effectiveTiming, effectiveEmailCsv, matchesDefault };
};

const getShiftTypeDescriptionByValue = function (shiftType, def = null) {
    const shiftTypeDescriptions = {
        fisso: 'Fisso',
        turno: 'Turno',
        a_chiamata: 'A Chiamata',
        senza_calendario: 'Senza Calendario',
    };

    return shiftTypeDescriptions[shiftType] || def;
};


const labels = {
    start_date: 'Data Inizio',
    end_date: 'Data Fine',
    shift_type: 'Tipologia Turno',
    week_hours: 'Ore Settimanali',
    typology: 'Tipologia Contrattuale',
    ccnl: 'CCNL',
    qualification: 'Qualifica Professionale',
    classification_level: 'Livello Inquadramento',
    working_hours_type: 'Tipo Orario',
    ral: 'Retribuzione (RAL)',
    // hourly_labor_cost: 'Costo orario del lavoro',
    social_security_institution: 'Ente Previdenziale',
    pat_inail: 'PAT Inail',
    is_working_partner: 'Socio Lavoratore',
    is_mobile_worker: 'Lavoratore in Mobilità',
    is_seasonal_work: 'Lavoro Stagionale',
    is_agriculture: 'Lavoro in Agricoltura',    
};

function formatDateToDisplay(date) {
    const d = new Date(date);
    if (isNaN(d)) return '';
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

const asNonPaginatedResponse = async (promise) => {
    const data = await promise;

    return {
        results: Array.isArray(data) ? data : [],
        next: null,
    };
};


function ContractForm({employeeId, initialValues, successCallback, undoCallback, setUnsavedChanges, employeeIsActive}) {
    const { token, profile } = useContext(AuthContext);
    const defaultValues = {
        start_date: '',
        end_date: '',
        shift_type: 'fisso',
        week_hours: '',
        typology: '',
        ccnl: '',
        qualification: '',
        classification_level: '',
        working_hours_type: '',
        ral: '',
        // hourly_labor_cost: '',
        social_security_institution: '',
        pat_inail: '',
        is_working_partner: '',
        is_mobile_worker: '',
        is_seasonal_work: '',
        is_agriculture: '',
        notification_timing: '',
        notification_email: '',
    };

    const [formData, setFormData] = useState(() => ({
        ...defaultValues,
        ...initialValues,
        start_date: initialValues?.start_date?.slice(0, 10) || '',
        end_date: initialValues?.end_date?.slice(0, 10) || '',

        notification_timing:
          initialValues?.notification_timing === null || initialValues?.notification_timing === undefined
            ? ""
            : String(initialValues.notification_timing),
        notification_email:
          initialValues?.notification_email === null || initialValues?.notification_email === undefined
            ? ""
            : String(initialValues.notification_email || "").trim(),
    }));

    const [fieldErrors, setFieldErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [classificationLevels, setClassificationLevels] = useState([]);
    const [selectedCcnlId, setSelectedCcnlId] = useState(null);
    const [selectedCcnl, setSelectedCcnl] = useState(initialValues?.ccnl || null); 
    const [selectedClassificationLevelId, setSelectedClassificationLevelId] = useState(null);
    const [selectedTypology, setSelectedTypology] = useState(typeof formData.typology === "object" ? formData.typology : null);
    const [formValues, setFormValues] = useState(initialValues);
    const [initialValuesCcnl, setInitialValuesCcnl] = useState({ ccnl: {}, classification_level: null });
    const startDateRef = useRef(null);
    const endDateRef = useRef(null);
    const ralRef = useRef(null);
    const patInailRef = useRef(null);
    const weekHoursRef = useRef(null);
    const [wwhEmployee, setWwhEmployee] = useState(null);
    const [recipientsDefaults, setRecipientsDefaults] = useState(null);
    const [defaultsLoaded, setDefaultsLoaded] = useState(false);

    const levelValues = Object.values(profile?.level || {}).flat();
    const hasShiftsLevel = levelValues.includes("shifts");

    useEffect(() => {
        if (initialValues && initialValues.ccnl) {
            setSelectedCcnlId(initialValues.ccnl.id);
        }
    }, [initialValues]);

    useEffect(() => {
      if (!token || !employeeId) return;

      let cancelled = false;
      setDefaultsLoaded(false);

      (async () => {
        try {
          const data = await getContractRecipientsDefault(token, employeeId);
          if (!cancelled) setRecipientsDefaults(data || null);
        } catch {
          if (!cancelled) setRecipientsDefaults(null);
        } finally {
          if (!cancelled) setDefaultsLoaded(true);
        }
      })();

      return () => { cancelled = true; };
    }, [token, employeeId]);

    const defaultTimingStr = useMemo(() => {
      const d = recipientsDefaults?.default_days ?? null;
      return d == null ? "" : String(d);
    }, [recipientsDefaults]);

    const defaultEmailCsv = useMemo(() => {
      if (!recipientsDefaults) return "";
      return buildDefaultEmailCsv({
        send_company: !!recipientsDefaults?.send_company,
        send_handlers: !!recipientsDefaults?.send_handlers,
        company_email: String(recipientsDefaults?.company_email || "").trim(),
        handlers_emails: Array.isArray(recipientsDefaults?.handlers_emails) ? recipientsDefaults.handlers_emails : [],
        others_emails: String(recipientsDefaults?.others_emails || ""),
      });
    }, [recipientsDefaults]);

    const shownTiming = String(formData.notification_timing || defaultTimingStr || "").trim();
    const shownEmail = String(formData.notification_email || defaultEmailCsv || "").trim();

    const reminderEnabled = useMemo(() => {
      const n = clampTiming(shownTiming);
      return n != null && n >= 1;
    }, [shownTiming]);

    const hasEndDate = useMemo(() => {
      return String(formData.end_date ?? "").trim() !== "";
    }, [formData.end_date]);

    useEffect(() => {
      if (hasEndDate) return;

      setFormData((prev) => ({
        ...prev,
        notification_timing: "",
        notification_email: "",
      }));

      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next.notification_timing;
        delete next.notification_email;
        return next;
      });
    }, [hasEndDate]);

    const companyEmail = String(recipientsDefaults?.company_email || "").trim();
    const companyName = String(recipientsDefaults?.company_label || "Azienda").trim();
    const handlers = Array.isArray(recipientsDefaults?.handlers_emails) ? recipientsDefaults.handlers_emails : [];
    const othersEmails = String(recipientsDefaults?.others_emails || "");

    useEffect(() => {
        if (!selectedCcnlId) {
            setClassificationLevels([]);
            const classification_level = initialValues?.classification_level || null
            setFormData(prevState => ({
                ...prevState,
                classification_level: classification_level
            }));
            

            setSelectedClassificationLevelId(classification_level);
            return;
        }
    
        if (initialValuesCcnl.ccnl?.id === selectedCcnlId && initialValuesCcnl.classification_level) {
            setClassificationLevels(prevLevels => prevLevels);
            setFormData(prevState => ({
                ...prevState,
                classification_level: initialValuesCcnl.classification_level
            }));
            setSelectedClassificationLevelId(initialValuesCcnl.classification_level?.id || null);          
            
        } else {
            getClassificationLevelListPaginated(token, selectedCcnlId).then((data) => {
                setClassificationLevels(data);
                const classification_level = initialValues?.classification_level || null
                setFormData(prevState => ({
                    ...prevState,
                    classification_level: classification_level
                }));
                setSelectedClassificationLevelId(classification_level);
            });
        }
    }, [selectedCcnlId, token, initialValuesCcnl]);

    useEffect(() => {
        const fetchEmployeeWeeklyWorkingHourData = async () => {
            try {
                const employeeWeeklyWorkingHourData = await getEmployeeWeeklyWorkingHour(token, employeeId, 'shifts');
                if (employeeWeeklyWorkingHourData && employeeWeeklyWorkingHourData.weekly_working_hour) {
                    setWwhEmployee(employeeWeeklyWorkingHourData.weekly_working_hour || []);
                }
            } catch (error) {
                console.error('Errore durante il recupero dell\'orario lavorativo', error);
            }
        };
        fetchEmployeeWeeklyWorkingHourData();
    }, [token, employeeId]);

    const requiredFields = [
        'start_date',
        'shift_type',
    ];


    const isValidDate = (dateString) => {
        if (!dateString) return false;
        const regex = /^\d{4}-\d{2}-\d{2}$/;
        if (!regex.test(dateString)) return false;
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        return (
            date.getFullYear() === year &&
            date.getMonth() + 1 === month &&
            date.getDate() === day
        );
    };
    
    const saveHandler = function () {
        let error = false;
        setIsSaving(true);
        
        requiredFields.forEach(requiredField => {
            if (!formData[requiredField]) {
                error = true;
                setFieldErrors(currentValue => ({
                    ...currentValue,
                    [requiredField]: 'Campo Obbligatorio'
                }));
            }
        });
    
        if (formData.start_date) {
            if (!isValidDate(formData.start_date)) {
                error = true;
                setFieldErrors(currentValue => ({
                    ...currentValue,
                    start_date: 'Seleziona una data di inizio valida'
                }));
            }
        }
    
        if (formData.end_date) {
            if (!isValidDate(formData.end_date)) {
                error = true;
                setFieldErrors(currentValue => ({
                    ...currentValue,
                    end_date: 'Seleziona una data di fine valida'
                }));
            }
        }

        if (formData.week_hours) {
            const weekHoursRegex = /^\d+(\.\d{0,2})?$/;
            if (!weekHoursRegex.test(formData.week_hours)) {
                error = true;
                setFieldErrors(currentValue => ({
                    ...currentValue,
                    week_hours: 'Formato non valido, usa numeri con massimo due cifre decimali'
                }));
            }
        }

        if ((formData.week_hours === null || formData.week_hours === '' || formData.week_hours === undefined) && wwhEmployee) {
            error = true;
            setFieldErrors(currentValue => ({
                ...currentValue,
                week_hours: 'Non è possibile salvare un valore vuoto perchè è presente un turno lavorativo impostato.'
            }));
        }

        if (formData.ral && !validateRal(formData.ral)) {
            error = true;
            setFieldErrors(currentValue => ({
                ...currentValue,
                ral: 'Formato non valido, usa numeri con massimo due cifre decimali'
            }));
        }

        if (formData.hourly_labor_cost && !validateRal(formData.hourly_labor_cost)) {
            error = true;
            setFieldErrors(currentValue => ({
                ...currentValue,
                hourly_labor_cost: 'Formato non valido, usa numeri con massimo due cifre decimali'
            }));
        }

        if (
            formData.start_date &&
            formData.end_date &&
            isValidDate(formData.start_date) &&
            isValidDate(formData.end_date)
        ) {
            if (formData.end_date < formData.start_date) {
                error = true;

                setFieldErrors(currentValue => ({
                    ...currentValue,
                    end_date: 'La data di fine non può essere precedente alla data di inizio'
                }));
            }
        }

        if (formData.pat_inail && (formData.pat_inail.length < 8 || formData.pat_inail.length > 10)) {
            error = true;
            setFieldErrors(currentValue => ({
                ...currentValue,
                pat_inail: 'Le cifre devono essere minimo 8 e massimo 10'
            }));
        }
        
        if (hasEndDate && defaultsLoaded) {
          const { effectiveTiming, effectiveEmailCsv } = buildEffectiveNotifications({
            timingInput: formData.notification_timing,   // "" => default
            emailInput: formData.notification_email,     // "" => default
            defaultTimingStr,
            defaultEmailCsv,
          });

          const timingEnabled = effectiveTiming != null && effectiveTiming >= 1;

          if (timingEnabled) {
            if (!effectiveEmailCsv) {
              error = true;
              setFieldErrors((cur) => ({
                ...cur,
                notification_email: "Inserisci almeno una mail per usare il promemoria.",
              }));
            } else {
              const invalids = splitEmails(effectiveEmailCsv).filter((e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
              if (invalids.length) {
                error = true;
                setFieldErrors((cur) => ({
                  ...cur,
                  notification_email: `Email non valide: ${invalids.join(", ")}`,
                }));
              }
            }
          }
        }
        
        if (error) {
            setIsSaving(false);

            const fieldErrorsList = Object.keys(fieldErrors);
            if (fieldErrorsList.includes('start_date')) {
                startDateRef.current.scrollIntoView({ behavior: 'smooth' });
            } else if (fieldErrorsList.includes('end_date')) {
                endDateRef.current.scrollIntoView({ behavior: 'smooth' });
            } else if (fieldErrorsList.includes('week_hours')) {
                weekHoursRef.current.scrollIntoView({ behavior: 'smooth' });
            } else if (fieldErrorsList.includes('ral')) {
                ralRef.current.scrollIntoView({ behavior: 'smooth' });
            } else if (fieldErrorsList.includes('pat_inail')) {
                patInailRef.current.scrollIntoView({ behavior: 'smooth' });
            }
            return;
        }

        const formDataToSend = {
            ...formData,
            start_date: formData.start_date,
            end_date: formData.end_date || null,
        };

        if (hasEndDate && defaultsLoaded) {
          const { effectiveTiming, effectiveEmailCsv, matchesDefault } = buildEffectiveNotifications({
            timingInput: formData.notification_timing,
            emailInput: formData.notification_email,
            defaultTimingStr,
            defaultEmailCsv,
          });

          if (matchesDefault) {
            formDataToSend.notification_timing = null;
            formDataToSend.notification_email = null;
          } else {
            formDataToSend.notification_timing = effectiveTiming;          // number|null
            formDataToSend.notification_email = effectiveEmailCsv || null; // string|null
          }
        } else {
          // se non c'è end_date -> niente promemoria
          formDataToSend.notification_timing = null;
          formDataToSend.notification_email = null;
        }

        Object.keys(formDataToSend).forEach(key => {
            if (formDataToSend[key] && formDataToSend[key].hasOwnProperty('id')) {
                formDataToSend[key] = formDataToSend[key].id;
            }
            
            if (formDataToSend[key] === '' || formDataToSend[key] === null || formDataToSend[key] === undefined) {
                formDataToSend[key] = null;
            }
        });

        if (initialValues && initialValues.id) {
            updateEmployeeContract(token, employeeId, initialValues.id, formDataToSend).then(
                res => {
                    Swal.fire({
                        title: "Contratto modificato con successo!",
                        icon: "success",
                    });
        
                    if(successCallback)
                        successCallback();
                        setIsSaving(false);
                        setUnsavedChanges(false);
                })
                .catch(error => {
                    setIsSaving(false);
        
                    if (error.response.data && typeof error.response.data === 'object') {
                        const errorMessages = error.response.data.non_field_errors;
                        
                        if (errorMessages && errorMessages[0] === "Il tuo abbonamento non consente l'inserimento di nuovi contratti attivi") {
                            Swal.fire({
                                icon: "error",
                                title: "Oops...",
                                html: `${errorMessages[0]}`,
                                showCancelButton: true,
                                confirmButtonText: 'Gestisci abbonamento',
                                cancelButtonText: 'Annulla',
                                cancelButtonColor: '#d1d5db',
                                allowOutsideClick: false,
                            }).then((result) => {
                                if (result.isConfirmed) {
                                    window.location.href = "/livelli";
                                }
                            });
                        } else {
                            const formattedErrors = Object.entries(error.response.data)
                                .map(([field, messages]) => {
                                    return messages.length > 0 ? `${field}: ${messages[0]}` : '';
                                })
                                .filter(message => message)
                                .join('\n');
                            
                            Swal.fire({
                                icon: "error",
                                title: "Oops...",
                                text: `Errore durante il salvataggio:\n${formattedErrors}`,
                            });
                        }
                    } else {
                        Swal.fire({
                            icon: "error",
                            title: "Oops...",
                            text: 'Errore durante il salvataggio. Controlla i dettagli.',
                        });
                    }
                });
            return;
        }

        addEmployeeContract(token, employeeId, formDataToSend)
        .then((res) => {
            Swal.fire({
                title: "Contratto creato con successo!",
                icon: "success",
            });
    
            if (successCallback) successCallback();
            setIsSaving(false);
            setUnsavedChanges(false);
        })
        .catch((error) => {
            console.error('Errore durante la creazione del contratto:', error);
    
            setIsSaving(false);
    
            let errorMessage = 'Si è verificato un errore durante il salvataggio. Per favore riprova.';
            if (error.response && error.response.data) {
                const errorData = error.response.data;
                const errorMessages = [];

                Object.keys(errorData).forEach((key) => {
                    const messages = errorData[key];
                    if (Array.isArray(messages)) {
                        errorMessages.push(...messages); 
                    } else if (typeof messages === 'string') {
                        errorMessages.push(messages);
                    }
                });
    
                if (errorMessages.length > 0) {
                    errorMessage = errorMessages.join(' ');
                }

                if (errorMessages.includes("Il tuo abbonamento non consente l'inserimento di nuovi contratti attivi")) {
                    Swal.fire({
                        icon: "error",
                        title: "Oops...",
                        html: "Il tuo abbonamento non consente l'inserimento di nuovi contratti attivi",
                        showCancelButton: true,
                        confirmButtonText: 'Gestisci abbonamento',
                        cancelButtonText: 'Annulla',
                        cancelButtonColor: '#d1d5db',
                        allowOutsideClick: false,
                    }).then((result) => {
                        if (result.isConfirmed) {
                            window.location.href = "/livelli";
                        }
                    });
                    return;
                }
            }
    
            Swal.fire({
                icon: "error",
                title: "Errore",
                text: errorMessage,
                confirmButtonText: "OK",
            });
        });    
    };

    const onChangeHandler = function (fieldName, event) {
        const value = event.target.value === '' ? null : event.target.value;
        
        setFieldErrors(currentValue => {
            delete currentValue[fieldName];

            return currentValue;
        })
        setFormData(
            (currentData) => {
                return {...currentData, [fieldName]: event.target.value};
            }
        );
        setUnsavedChanges(true);
    };

    const onListChangeHandler = function (fieldName, value) {
        setFieldErrors(currentValue => ({
            ...currentValue,
            [fieldName]: null,
        }));
    
        setFormData(currentData => ({
            ...currentData,
            [fieldName]: value !== null ? value : null,
        }));
    
        setUnsavedChanges(true);
    };   
    
    const handleComboBoxChange = (field, res) => {
        setFormData((prev) => ({
            ...prev,
            [field]: res || null,
            ...(field === "ccnl" ? { classification_level: null } : {}),
        }));

        if (field === "ccnl") {
            setSelectedCcnlId(res?.id || null);
            setSelectedClassificationLevelId(null);
        }

        setUnsavedChanges(true);
    };

    const handleChange = (field, e) => {
        let value = e.target.value;
        if (field === 'ral' || field === 'hourly_labor_cost') {
            value = value.replace(',', '.');
            if (value.includes('.')) {
                const parts = value.split('.');
                value = parts[0] + '.' + parts[1].substring(0, 2);
            }
        } else if (field === 'pat_inail') {
            value = value.replace(/[^0-9]/g, '');
        }

        setFormData({
            ...formData,
            [field]: value
        });
        setUnsavedChanges(true);
    };

    const validateRal = (value) => {
        const regex = /^\d+(\.\d{1,2})?$/;
        return regex.test(value);
    };

    const fields = {
        start_date: (
            <input
                type="date"
                name="start_date"
                value={formData.start_date || ''}
                onChange={(e) => onChangeHandler('start_date', e)}
                required
                ref={startDateRef}
                className="shadow-md border border-gray-300 rounded-md
                        w-full py-2 px-3 text-gray-700 bg-white
                        focus:ring-indigo-400 focus:border-indigo-400
                        cursor-pointer"
            />
        ),
        end_date: (
            <>
                <input
                    type="date"
                    name="end_date"
                    value={formData.end_date || ''}
                    min={formData.start_date || undefined}
                    onChange={(e) => onChangeHandler('end_date', e)}
                    ref={endDateRef}
                    className="shadow-md border border-gray-300 rounded-md
                            w-full py-2 px-3 text-gray-700 bg-white
                            focus:ring-indigo-400 focus:border-indigo-400
                            cursor-pointer"
                />
                <div className="col-span-3 flex items-center text-end">
                    <p className="text-xs w-full">
                        *Non effettuando la selezione della Data Fine il contratto
                        verrà riconosciuto come{' '}
                        <span className="underline">Indeterminato</span>
                    </p>
                </div>
            </>
        ),
        shift_type: <ListBox
            options={[
                { name: 'Fisso', value: 'fisso' },
                { name: 'Turno', value: 'turno' },
                { name: 'Senza Calendario', value: 'senza_calendario' },
                { name: 'A Chiamata', value: 'a_chiamata' }
            ]}
            defaultValue={formData['shift_type'] ? 
                { name: getShiftTypeDescriptionByValue(formData['shift_type']), value: formData['shift_type'] } : undefined}
            onChange={(selectedOption) => {
                onListChangeHandler('shift_type', selectedOption.value);
            }}
            required
        />,
        week_hours: <input 
            value={formData['week_hours'] || ''} 
            type="number" 
            className='shadow-md appearance-none border border-gray-300 rounded w-full py-2 px-3 text-gray-700 leading-tight focus:ring-indigo-400 focus:border-indigo-400' 
            name="week_hours" 
            step="0.01"
            onChange={(e) => onChangeHandler('week_hours', e)} 
            ref={weekHoursRef}
            min="0"
        />,
        typology: (
            <AsyncSingleSelectSearchbar
                token={token}
                value={selectedTypology}
                onChange={(typology) => {
                    setSelectedTypology(typology);

                    handleComboBoxChange("typology", typology);

                    setFormData((prev) => ({
                        ...prev,
                        typology: typology || null,
                    }));

                    setUnsavedChanges(true);
                }}
                fetcher={({ token, q, page, pageSize }) =>
                    getContractTypesListPaginated(token, q, page, pageSize)
                }
                getOptionValue={(typology) => typology.id}
                getOptionLabel={(typology) =>
                    `${typology?.code || ""} - ${typology?.description || ""}`
                }
                placeholder="Seleziona tipologia contrattuale"
            />
        ),
        ccnl: (
            <AsyncSingleSelectSearchbar
                token={token}
                value={selectedCcnl}
                onChange={(ccnl) => {
                    setSelectedCcnl(ccnl);

                    handleComboBoxChange("ccnl", ccnl);
                    setSelectedCcnlId(ccnl?.id || null);

                    setFormData((prev) => ({
                        ...prev,
                        ccnl: ccnl || null,
                        classification_level: null,
                }));

                setSelectedClassificationLevelId(null);
                setUnsavedChanges(true);
                }}
                fetcher={({ token, q, page, pageSize }) =>
                    getCCNLListPaginated(token, q, page, pageSize)
                }
                getOptionValue={(ccnl) => ccnl.id}
                getOptionLabel={(ccnl) => `${ccnl?.codice || ""} - ${ccnl?.nome || ""}`}
                placeholder="Seleziona CCNL"
            />
        ),     
        classification_level: (
            <AsyncSingleSelectSearchbar
                token={token}
                value={
                    typeof formData.classification_level === "object"
                        ? formData.classification_level
                        : null
                }
                onChange={(level) => {
                    handleComboBoxChange("classification_level", level);
                    setSelectedClassificationLevelId(level?.id || null);
                    setUnsavedChanges(true);
                }}
                fetcher={({ token, q, page, pageSize }) => {
                    if (!selectedCcnlId) {
                        return Promise.resolve({ results: [], next: null });
                    }

                    return getClassificationLevelListPaginated(
                        token,
                        selectedCcnlId,
                        q,
                        page,
                        pageSize
                    );
                }}
                getOptionValue={(level) => level.id}
                getOptionLabel={(level) =>
                `${level?.cod_livello || ""} - ${level?.des_livello || ""}`
                }
                placeholder={
                    selectedCcnlId
                        ? "Seleziona livello inquadramento"
                        : "Seleziona prima un CCNL"
                }
            />
        ),
        qualification: (
            <AsyncSingleSelectSearchbar
                token={token}
                value={
                    typeof formData.qualification === "object"
                        ? formData.qualification
                        : null
                }
                onChange={(qualification) => {
                    handleComboBoxChange("qualification", qualification);
                    setUnsavedChanges(true);
                }}
                fetcher={({ token, q, page, pageSize }) =>
                    getWorkTaskListPaginated(token, q, page, pageSize)
                }
                getOptionValue={(qualification) => qualification.id}
                getOptionLabel={(qualification) =>
                `${qualification?.codice || ""} - ${qualification?.descrizione || ""}`
                }
                placeholder="Seleziona qualifica"
            />
        ),
        working_hours_type: (
            <AsyncSingleSelectSearchbar
                token={token}
                value={
                    typeof formData.working_hours_type === "object"
                        ? formData.working_hours_type
                        : null
                }
                onChange={(workingHour) => {
                    handleComboBoxChange("working_hours_type", workingHour);
                    setUnsavedChanges(true);
                }}
                fetcher={({ token, q, page, pageSize }) =>
                    getWorkingHourTypesListPaginated(token, q, page, pageSize)
                }
                getOptionValue={(workingHour) => workingHour.id}
                getOptionLabel={(workingHour) =>
                    `${workingHour?.codice || ""} - ${workingHour?.nome || ""}`
                }
                placeholder="Seleziona tipo orario"
            />
        ),
        ral: <>  
            <input
                value={formData['ral'] || ''}
                required
                type="text"
                className='shadow-md appearance-none border border-gray-300 rounded w-full py-2 px-3 text-gray-700 leading-tight focus:ring-indigo-400 focus:border-indigo-400'
                name="ral"
                onChange={(e) => handleChange('ral', e)}
                placeholder='00.00'
            />
            {fieldErrors.ral && <span className="text-red-500">{fieldErrors.ral}</span>}
        </>,
        // hourly_labor_cost: <>
        //     <input
        //         value={formData['hourly_labor_cost'] || ''}
        //         type="text"
        //         className='shadow appearance-none border border-gray-300 rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
        //         name="hourly_labor_cost"
        //         onChange={(e) => handleChange('hourly_labor_cost', e)}
        //         placeholder='00.00'
        //     />
        //     {fieldErrors.hourly_labor_cost && <span className="text-red-500">{fieldErrors.hourly_labor_cost}</span>}
        // </>,
        social_security_institution: (
            <AsyncSingleSelectSearchbar
                token={token}
                value={
                    typeof formData.social_security_institution === "object"
                        ? formData.social_security_institution
                        : null
                }
                onChange={(ente) => {
                    handleComboBoxChange("social_security_institution", ente);
                    setUnsavedChanges(true);
                }}
                fetcher={({ token, q, page, pageSize }) =>
                    getEntePrevidenzialeListPaginated(token, q, page, pageSize)
                }
                getOptionValue={(ente) => ente.id}
                getOptionLabel={(ente) =>
                `${ente?.code || ""} - ${ente?.description || ""}`
                }
                placeholder="Seleziona ente previdenziale"
            />
        ),
        pat_inail: <>
            <input 
                type="text" 
                value={formData['pat_inail'] || ''} 
                className='shadow-md appearance-none border border-gray-300 rounded w-full py-2 px-3 text-gray-700 leading-tight focus:ring-indigo-400 focus:border-indigo-400' 
                name="pat_inail"
                minLength={8}
                maxLength={10} 
                onChange={(e) => handleChange('pat_inail', e)}
                ref={patInailRef} 
            />
            {fieldErrors.ral && <span className="text-red-500">{fieldErrors.ral}</span>}
        </>,
        is_working_partner: (
            <ListBox
                options={[
                    { name: '-', value: null },
                    { name: 'SI', value: true },
                    { name: 'NO', value: false },
                ]}
                defaultValue={
                    formData['is_working_partner'] === null
                        ? { name: '-', value: null }
                        : {
                            name: formData['is_working_partner'] === true ? 'SI' : 'NO',
                            value: formData['is_working_partner'],
                        }
                }
                onChange={(selectedOption) => {
                    onListChangeHandler('is_working_partner', selectedOption.value);
                }}
            />
        ),
        is_mobile_worker: (
            <ListBox
                options={[
                    { name: '-', value: null },
                    { name: 'SI', value: true },
                    { name: 'NO', value: false },
                ]}
                defaultValue={
                    formData['is_mobile_worker'] !== null
                        ? {
                            name: formData['is_mobile_worker'] === true ? 'SI' : 'NO',
                            value: formData['is_mobile_worker'],
                        }
                        : { name: '-', value: null }
                }
                onChange={(selectedOption) => {
                    onListChangeHandler('is_mobile_worker', selectedOption.value);
                }}
            />
        ),

        is_seasonal_work: <ListBox
            options={[
                { name: '-', value: null },
                { name: 'SI', value: true },
                { name: 'NO', value: false },
            ]}
            defaultValue={
                formData['is_seasonal_work'] !== null
                    ? {
                        name: formData['is_seasonal_work'] === true ? 'SI' : 'NO',
                        value: formData['is_seasonal_work'],
                    }
                    : { name: '-', value: null }
            }
            onChange={(selectedOption) => {
                onListChangeHandler('is_seasonal_work', selectedOption.value);
            }}
        />,
        is_agriculture: (
            <ListBox
                options={[
                    { name: '-', value: null },
                    { name: 'SI', value: true },
                    { name: 'NO', value: false },
                ]}
                defaultValue={
                    formData['is_agriculture'] !== null
                        ? {
                            name: formData['is_agriculture'] === true ? 'SI' : 'NO',
                            value: formData['is_agriculture'],
                        }
                        : { name: '-', value: null }
                }
                onChange={(selectedOption) => {
                    onListChangeHandler('is_agriculture', selectedOption.value);
                }}
                openDirection='down'
            />
        ),
    };

    return <>
        <form className='m-8'>
            {
                Object.keys(fields).map((fieldNameKey) => {
                    const fieldName = fieldNameKey;
                    const field = fields[fieldName];
                    const isRequired = requiredFields.includes(fieldName);
                    
                    return <div key={fieldName}>
                        <div className='grid grid-cols-3 gap-3'>
                            <div className='col-span-1 items-center flex mb-3'>
                                <label className="font-semibold text-sm text-gray-700">
                                    {labels[fieldName]} {isRequired && ' *'}
                                </label>
                            </div>
                            <div className='col-span-2 mb-3'>
                                {field}
                                {fieldErrors[fieldName] && (
                                    <div className="col-span-3 flex items-center text-center">
                                        <p className='text-xs text-red-500 font-semibold ml-1'>{fieldErrors[fieldName]}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                })
            }

            {hasEndDate && (
              <>
                <div className="mt-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-0">
                    Promemoria scadenza
                  </label>
                  <div className="text-gray-500 font-normal text-xs mb-1">
                    Giorni da 1 a 31, campo vuoto per l&apos;impostazione predefinita
                    {defaultTimingStr && (
                    <span className="pl-2">({defaultTimingStr} giorni)</span>
                    )}
                  </div>

                  {!defaultsLoaded ? (
                    <Fetching />
                  ) : (
                    <>
                      <input
                        name="notification_timing"
                        type="text"
                        inputMode="numeric"
                        placeholder={defaultTimingStr ? `${defaultTimingStr} giorni` : "Inserire giorni"}
                        value={formData.notification_timing ?? ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v !== "" && !/^\d+$/.test(v)) return;

                          setFieldErrors((prev) => {
                            const next = { ...prev };
                            delete next.notification_timing;
                            delete next.notification_email;
                            return next;
                          });

                          setFormData((prev) => ({ ...prev, notification_timing: v }));
                          setUnsavedChanges(true);
                        }}
                        onBlur={() => {
                          const raw = String(formData.notification_timing || "").trim();
                          if (raw === "") return;
                          const n = clampTiming(raw);
                          if (n != null) setFormData((prev) => ({ ...prev, notification_timing: String(n) }));
                        }}
                        className="w-full h-10 px-3 rounded-md shadow-sm border border-gray-300
                          hover:border-indigo-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200
                          text-gray-700 text-md bg-white"
                      />

                      {!!fieldErrors?.notification_timing && (
                        <p className="text-xs text-red-600 mt-1">
                          {Array.isArray(fieldErrors.notification_timing)
                            ? fieldErrors.notification_timing.join(", ")
                            : String(fieldErrors.notification_timing)}
                        </p>
                      )}
                        {hasEndDate && !employeeIsActive && (
                            <p className="mt-4 text-xs text-orange-600 bg-orange-50 border border-orange-200 rounded px-3 py-2">
                            Il dipendente non è attualmente attivo. Puoi configurare la scadenza e la relativa notifica, che verrà inviata solo se il dipendente sarà attivo al momento dell'invio.
                            </p>
                        )}
                    </>
                  )}
                </div>

                {reminderEnabled && (
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email di notifica scadenza
                    </label>

                    {!defaultsLoaded ? (
                      <Fetching />
                    ) : (
                      <>
                        <RecipientsField
                          token={token}
                          value={shownEmail}
                          onChange={(commaSeparated) => {
                            setFormData((prev) => ({ ...prev, notification_email: commaSeparated }));
                            setUnsavedChanges(true);
                            setFieldErrors((prev) => {
                              const next = { ...prev };
                              delete next.notification_email;
                              return next;
                            });
                          }}
                          isEdit={true}
                          disabled={false}
                          openDirection="down"
                          companyEmail={companyEmail}
                          companyName={companyName}
                          handlers={handlers}
                          othersEmails={othersEmails}
                          className="bg-gray-100 rounded-md pb-2 pt-2"
                          hasShiftsLevel={hasShiftsLevel}
                        />

                        {!!fieldErrors?.notification_email && (
                          <p className="text-xs text-red-600 mt-1">
                            {Array.isArray(fieldErrors.notification_email)
                              ? fieldErrors.notification_email.join(", ")
                              : String(fieldErrors.notification_email)}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </>
            )}
        </form>

        <div className='col-span-3 my-4 mx-8 flex justify-center'>
            <button
                className={`font-bold py-2 px-4 rounded-md text-white text-sm mr-4 w-24 ${
                    isSaving ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-500 text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2'
                  }`} 
                onClick={saveHandler}
                disabled={isSaving}
                type="button"
            >
                {isSaving ? 'Salvataggio...' : 'Salva'}
            </button>
            { 
                undoCallback && 
                <button 
                className='border block rounded-md bg-grey-400 px-3 py-2 mr-2 text-center text-sm font-semibold text-grey-800 shadow-sm hover:bg-grey-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 w-24'
                onClick={() => {
                    undoCallback();
                    setUnsavedChanges(false);
                }}
                type="button"
            >
                Annulla
            </button>
            }
        </div>
    </>
}


export default function ContractTabEmployee({employeeId, setUnsavedChanges, employee, refreshEmployee}) {
    const { token, profile } = useContext(AuthContext)
    const [contracts, setContracts] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [inEdit, setInEdit] = useState(null);
    const [profileContractId, setProfileContractId] = useState(null);
    const [justificationProfiles, setJustificationProfiles] = useState([]);
    const [savingProfileContractId, setSavingProfileContractId] = useState(null);
    const [selectedJustificationProfile, setSelectedJustificationProfile] = useState(null);
    const employeeIsActive = !!employee?.app_activate && !!employee?.active_contract;
    
    const canManageJustifications =
        profile.customer_type !== 'employee' ||
        profile.employee?.permissions?.includes('hr.manage_justification_all') ||
        profile.employee?.permissions?.includes('hr.manage_justification_ou');

    useEffect(() => {
        if(!employeeId) {
            return;
        }

       getContractList();

    }, [employeeId]);

    const getContractList = function () {
        getEmployeeContractsList(token, employeeId, 'payrolls').then(data => {
            setContracts(data || []);
        });
    }

    useEffect(() => {
        const fetchJustificationProfiles = async () => {
            try {
                const response = await getJustificationProfiles(token);
                setJustificationProfiles(Array.isArray(response) ? response : response?.results || []);
            } catch (error) {
                console.error('Errore durante il recupero dei profili giustificativi:', error);
            }
        };

        if (token) fetchJustificationProfiles();
    }, [token]);

    const handleContractProfileChange = (contract, selectedProfile) => {
        if (!selectedProfile?.id) return;

        if (selectedProfile.id === contract.justification_profile?.id) {
            setSelectedJustificationProfile(null);
            return;
        }

        setSelectedJustificationProfile(selectedProfile);
    };

    const handleConfirmContractProfile = async (contract) => {
        if (!selectedJustificationProfile?.id) return;

        if (selectedJustificationProfile.id === contract.justification_profile?.id) {
            setSelectedJustificationProfile(null);
            setProfileContractId(null);
            return;
        }

        try {
            setSavingProfileContractId(contract.id);

            await updateJustificationProfile(
                token,
                selectedJustificationProfile.id,
                {contract: contract.id,}
            );

            setSelectedJustificationProfile(null);
            setProfileContractId(null);

            getContractList();

            const profilesResponse = await getJustificationProfiles(token);

            setJustificationProfiles(
                Array.isArray(profilesResponse)
                    ? profilesResponse
                    : profilesResponse?.results || []
            );

            Swal.fire({
                icon: 'success',
                title: contract.justification_profile
                    ? 'Profilo modificato'
                    : 'Profilo associato',
                text: contract.justification_profile
                    ? 'Il profilo giustificativi è stato sostituito correttamente'
                    : 'Il profilo giustificativi è stato associato correttamente',
                timer: 1800,
                showConfirmButton: false,
            });

        } catch (error) {
            console.error('Errore durante l\'associazione del profilo giustificativi:', error);

            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: v2EstraiMessaggiErrore(error),
            });

        } finally {
            setSavingProfileContractId(null);
        }
    };

    const handleRemoveContractProfile = async (contract) => {
        if (!contract.justification_profile?.id) return;

        try {
            setSavingProfileContractId(contract.id);

            await updateJustificationProfile(
                token,
                contract.justification_profile.id,
                {remove_contract: contract.id,}
            );

            setProfileContractId(null);
            setSelectedJustificationProfile(null);
            getContractList();

            const profilesResponse = await getJustificationProfiles(token);

            setJustificationProfiles(
                Array.isArray(profilesResponse)
                    ? profilesResponse
                    : profilesResponse?.results || []
            );

            Swal.fire({
                icon: 'success',
                title: 'Associazione rimossa',
                text: 'Il profilo giustificativi è stato rimosso dal contratto',
                timer: 1800,
                showConfirmButton: false,
            });

        } catch (error) {
            console.error('Errore durante la rimozione del profilo giustificativi:', error);

            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: v2EstraiMessaggiErrore(error),
            });

        } finally {
            setSavingProfileContractId(null);
        }
    };

    const formatters = {
        start_date: (value) => {
            if(!value) {
                return '';
            }
            
            return new Date(value).toLocaleDateString('it');
        },
        end_date: (value) => {
            if(!value) {
                return '';
            }
            
            return new Date(value).toLocaleDateString('it');
        },
        typology: (value)  => {
            if(!value) {
                return '';
            }
            
            return (value.description);
        },
        ccnl: (value) => value ? `${value?.codice} - ${value?.nome}` : '',
        classification_level: (value) => value ? `${value?.cod_livello} - ${value?.des_livello}` : '',
        qualification: (value) => value?.descrizione,
        social_security_institution: (value) => value?.description,
        shift_type: (value) => getShiftTypeDescriptionByValue(value),
        working_hours_type: (value) => value ? `${value?.codice} - ${value?.nome}` : '',
        is_working_partner: (value) => value === null || value === undefined ? '' : value ? 'SI' : 'NO',
        is_mobile_worker: (value) => value === null || value === undefined ? '' : value ? 'SI' : 'NO',
        is_seasonal_work: (value) => value === null || value === undefined ? '' : value ? 'SI' : 'NO',
        is_agriculture: (value) => value === null || value === undefined ? '' : value ? 'SI' : 'NO',
        is_mandatory_hiring: (value) => value === null || value === undefined ? '' : value ? 'SI' : 'NO', 
    };

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        if (queryParams.get('showForm') === 'true') {
          setShowForm(true);
          queryParams.delete('showForm');
          const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
          window.history.replaceState(null, '', newUrl);
        }
    }, []);
    
    const handleShowForm = () => {
        setShowForm(prevShowForm => !prevShowForm);
        setUnsavedChanges(false)
    };

    const onCreateSuccess = () => {
        setShowForm(false);
        getContractList();
        refreshEmployee?.();
        setUnsavedChanges(false);
    };

    const onUpdateSuccess = () => {
        setInEdit(null);
        getContractList();
        refreshEmployee?.();
        setUnsavedChanges(false);
    };

    const undoCallback = () => {
        setInEdit(null);
        getContractList();
        setUnsavedChanges(false);
    };


    const handleDeleteContract = async (contractId) => {
        try {
            const result = await Swal.fire({
                title: "Sei sicuro di voler eliminare il contratto?",
                showDenyButton: true,
                confirmButtonText: "Sì, elimina",
                denyButtonText: "Annulla",
                denyButtonColor: "#d6d3d1",
            });
    
            if (result.isConfirmed) {
                Swal.fire({
                    title: 'Eliminazione in corso...',
                    didOpen: () => {
                        Swal.showLoading();
                    },
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    allowEnterKey: false,
                });
    
                await deleteEmployeeContract(token, employeeId, contractId);
    
                Swal.fire({
                    title: "Contratto eliminato con successo!",
                    icon: "success",
                });
    
                const updatedContracts = await getEmployeeContractsList(token, employeeId);
                setContracts(updatedContracts);
            } else if (result.isDenied) {
                return;
            }
        } catch (error) {
            const errorMessage = estraiMessaggiErrore(error);
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: errorMessage,
            });
        }
    };

    const getContractJustificationProfile = (contract) => {
        if (!contract.justification_profile?.id) return null;

        return justificationProfiles.find(
            item => item.id === contract.justification_profile.id
        ) || contract.justification_profile;
    };

    return <>
        {
            inEdit === null &&
            <div className='flex justify-end my-4 me-5'>
                <button 
                    className="border border-gray-400 rounded-md bg-grey-400 me-1 px-3 py-2 text-center text-sm font-semibold text-grey-800 shadow-md hover:bg-grey-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-grey-600 w-60"
                    onClick={handleShowForm}
                    type="button"
                >
                    {showForm ? <><ArrowUturnLeftIcon className="h-4 w-4 inline-block mr-1" /> Annulla</> : <><PlusIcon className="h-4 w-4 inline-block mr-2" /> Aggiungi Nuovo Contratto</>}
                </button>
            </div>
        }
        {showForm && (
            <ContractForm employeeId={employeeId} successCallback={onCreateSuccess} setUnsavedChanges={setUnsavedChanges} employeeIsActive={employeeIsActive} />
        )}

        {/* VISUALIZZAZIONE DELLA TAB CONTRATTO ALL'INTERNO DI MODIFICA DIPENDENTE */}
        <div className="container w-full px-4 pb-4">
            {contracts
                .sort((a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime())
                .map(contract => (
                    <div
                        key={contract.id}
                        className="relative m-6 mb-4 overflow-visible rounded-md border border-gray-300 bg-white p-4 px-2 pt-3 shadow-sm sm:px-8 focus:ring-indigo-400 focus:border-2 focus:border-indigo-400 focus:ring-offset-1"
                    >
                        {inEdit === contract.id ? (
                            <ContractForm
                                employeeId={employeeId}
                                successCallback={onUpdateSuccess}
                                undoCallback={undoCallback}
                                initialValues={contract}
                                setUnsavedChanges={setUnsavedChanges}
                                employeeIsActive={employeeIsActive}
                            />
                        ) : (
                            <>
                                {/* HEADER */}
                                <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 pb-3">
                                    <div className="flex items-center gap-1.5">
                                        {contract.is_active ? (
                                            <CheckCircleIcon className="h-5 w-5 text-indigo-700" />
                                        ) : (
                                            <XCircleIcon className="h-5 w-5 text-gray-500" />
                                        )}

                                        <span className={`text-md font-semibold uppercase ${
                                            contract.is_active ? 'text-indigo-700' : 'text-gray-700'
                                        }`}>
                                            {contract.is_active ? 'Attivo' : 'Non Attivo'}
                                        </span>
                                    </div>

                                    {!showForm && (
                                        <div className="flex flex-wrap items-center justify-end gap-2">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setProfileContractId(null);
                                                    setInEdit(contract.id);
                                                }}
                                                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-xs font-semibold text-gray-700 shadow-sm transition-colors hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700"
                                            >
                                                <PencilIcon className="h-4 w-4" />
                                                Modifica
                                            </button>
                                            {canManageJustifications && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setSelectedJustificationProfile(null);
                                                        setProfileContractId(profileContractId === contract.id ? null : contract.id);
                                                    }}
                                                    className={`inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-xs font-semibold shadow-sm transition-colors ${
                                                        profileContractId === contract.id
                                                            ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                                                            : 'border-gray-300 bg-white text-gray-700 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700'
                                                    }`}
                                                >
                                                    <IdentificationIcon className="h-4 w-4" />
                                                    {contract.justification_profile ? 'Gestisci Giustificativi' : 'Associa Gruppo Giustificativi'}
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => handleDeleteContract(contract.id.toString())}
                                                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-xs font-semibold text-gray-700 shadow-sm transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                                Elimina
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* ASSOCIA / SOSTITUISCI PROFILO */}
                                {profileContractId === contract.id && (
                                    <div className="relative z-20 my-3 overflow-visible rounded-md border border-indigo-100 bg-indigo-50 p-3">

                                        <div className="mb-2 flex items-start justify-between gap-3">
                                            <div>
                                                <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                                                    {contract.justification_profile
                                                        ? 'Gestisci profilo giustificativi'
                                                        : 'Associa profilo giustificativi'}
                                                </span>

                                                {profile.customer_type === 'employee' && (
                                                    <p className="mt-1 text-xs font-normal normal-case text-gray-500">
                                                        Puoi gestire e modificare l'assegnazione del profilo a questo contratto.
                                                        Per creare nuovi gruppi di giustificativi, rivolgiti al gestore dell'account.
                                                    </p>
                                                )}
                                            </div>

                                            <div className="flex shrink-0 justify-end">
                                                {contract.justification_profile && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveContractProfile(contract)}
                                                        disabled={savingProfileContractId === contract.id}
                                                        className="me-2 inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-xs font-semibold text-red-600 transition-colors hover:border-red-300 hover:bg-red-50 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
                                                    >
                                                        Rimuovi associazione
                                                    </button>
                                                )}

                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setSelectedJustificationProfile(null);
                                                        setProfileContractId(null);
                                                    }}
                                                    disabled={savingProfileContractId === contract.id}
                                                    className="rounded-lg px-2 text-xs font-semibold text-gray-500 hover:border-gray-300 hover:bg-gray-200 hover:text-gray-700 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
                                                >
                                                    Chiudi
                                                </button>
                                            </div>
                                        </div>

                                        {justificationProfiles.length > 0 ? (
                                            <div className="relative z-[100]">
                                                <JustificationProfileListBox
                                                    profiles={justificationProfiles}
                                                    value={selectedJustificationProfile || contract.justification_profile || null}
                                                    onChange={(selectedProfile) => handleContractProfileChange(contract, selectedProfile)}
                                                    disabled={savingProfileContractId === contract.id}
                                                />
                                            </div>
                                        ) : (
                                            <div className="flex min-h-10 items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-400">
                                                {profile.customer_type === 'employee'
                                                    ? 'Non sono presenti Gruppi di Giustificativi disponibili. Per la creazione di un nuovo gruppo, rivolgiti al gestore dell\'account.'
                                                    : 'Non sono presenti Gruppi di Giustificativi. Puoi crearli nella sezione "Configurazione Parametri".'}
                                            </div>
                                        )}

                                        {/* PROFILO ATTUALMENTE ASSOCIATO */}
                                        {contract?.justification_profile && !selectedJustificationProfile && (() => {
                                            const savedProfile = getContractJustificationProfile(contract);

                                            return (
                                                <div className="mt-4 overflow-hidden rounded-md border border-gray-200 bg-white">
                                                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2.5">
                                                        <div className="min-w-0">
                                                            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                                Gruppo attualmente associato
                                                            </p>

                                                            <p className="mt-0.5 truncate text-sm font-semibold uppercase text-indigo-800">
                                                                {savedProfile?.name}
                                                            </p>
                                                        </div>

                                                        <span className="rounded-md bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700">
                                                            {savedProfile?.justifications?.length || 0}{' '}
                                                            {(savedProfile?.justifications?.length || 0) === 1
                                                                ? 'giustificativo'
                                                                : 'giustificativi'}
                                                        </span>
                                                    </div>

                                                    {savedProfile?.justifications?.length > 0 ? (
                                                        <div className="grid w-full grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
                                                            {savedProfile.justifications.map((item) => (
                                                                <div
                                                                    key={item.id}
                                                                    title={item.justification?.description || ''}
                                                                    className="
                                                                        flex min-w-0 items-center gap-2
                                                                        border-b border-r border-gray-200
                                                                        px-3 py-1.5
                                                                        transition-colors
                                                                        hover:bg-indigo-50/50
                                                                        sm:[&:nth-child(2n)]:border-r-0
                                                                        xl:[&:nth-child(2n)]:border-r
                                                                        xl:[&:nth-child(3n)]:border-r-0
                                                                    "
                                                                >
                                                                    <span className="w-[55px] shrink-0 text-[11px] font-bold uppercase tracking-wide text-indigo-700">
                                                                        {item.justification?.code}
                                                                    </span>

                                                                    <span className="min-w-0 truncate text-sm text-gray-700">
                                                                        {item.justification?.description || '—'}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div className="px-3 py-3 text-xs text-gray-500">
                                                            Il gruppo associato non contiene giustificativi
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}

                                        {/* ANTEPRIMA PROFILO SELEZIONATO */}
                                        {selectedJustificationProfile && (
                                            <div className="mt-4 overflow-hidden rounded-md border border-gray-200 bg-gray-50">

                                                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-3 py-2.5">
                                                    <div className="min-w-0">
                                                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                            Anteprima gruppo selezionato
                                                        </p>

                                                        <p className="mt-0.5 truncate text-sm font-semibold uppercase text-indigo-800">
                                                            {selectedJustificationProfile.name}
                                                        </p>
                                                    </div>

                                                    <span className="rounded-md bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700">
                                                        {selectedJustificationProfile.justifications?.length || 0}{' '}
                                                        {(selectedJustificationProfile.justifications?.length || 0) === 1
                                                            ? 'giustificativo'
                                                            : 'giustificativi'}
                                                    </span>
                                                </div>

                                                {selectedJustificationProfile.justifications?.length > 0 ? (
                                                    <div className="grid w-full grid-cols-1 bg-white sm:grid-cols-2 xl:grid-cols-3">
                                                        {selectedJustificationProfile.justifications.map((item) => (
                                                            <div
                                                                key={item.id}
                                                                title={item.justification?.description || ''}
                                                                className="
                                                                    flex min-w-0 items-center gap-2
                                                                    border-b border-r border-gray-200
                                                                    px-3 py-1.5
                                                                    transition-colors
                                                                    hover:bg-indigo-50/50
                                                                    sm:[&:nth-child(2n)]:border-r-0
                                                                    xl:[&:nth-child(2n)]:border-r
                                                                    xl:[&:nth-child(3n)]:border-r-0
                                                                "
                                                            >
                                                                <span className="w-[55px] shrink-0 text-[11px] font-bold uppercase tracking-wide text-indigo-700">
                                                                    {item.justification?.code}
                                                                </span>

                                                                <span className="min-w-0 truncate text-sm text-gray-700">
                                                                    {item.justification?.description || '—'}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="bg-white px-3 py-3 text-xs text-gray-500">
                                                        Il gruppo selezionato non contiene giustificativi
                                                    </div>
                                                )}

                                                <div className="flex justify-end gap-2 border-t border-gray-200 px-3 py-3">
                                                    <button
                                                        type="button"
                                                        onClick={() => setSelectedJustificationProfile(null)}
                                                        disabled={savingProfileContractId === contract.id}
                                                        className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-semibold text-gray-600 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                                    >
                                                        Annulla selezione
                                                    </button>

                                                    <button
                                                        type="button"
                                                        onClick={() => handleConfirmContractProfile(contract)}
                                                        disabled={savingProfileContractId === contract.id}
                                                        className="rounded-md bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                                                    >
                                                        {savingProfileContractId === contract.id
                                                            ? 'Salvataggio...'
                                                            : contract.justification_profile
                                                                ? 'Conferma sostituzione'
                                                                : 'Conferma assegnazione'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* DATI CONTRATTO */}
                                <div>
                                    {Object.keys(labels).map(contractProperty => {
                                        const formatter = formatters[contractProperty] || (value => value);
                                        const value = formatter(contract[contractProperty]);

                                        return (
                                            <div key={`${contract.id}-${contractProperty}`} className="grid grid-cols-3 gap-3">
                                                <div className="col-span-1 flex items-center pb-2">
                                                    <label className="text-sm font-semibold text-gray-700">
                                                        {labels[contractProperty]}
                                                    </label>
                                                </div>

                                                <div className="col-span-2 pb-2 text-sm font-semibold text-gray-700">
                                                    {value}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* PROFILO ATTUALMENTE ASSOCIATO */}
                                <div className="grid grid-cols-3 gap-3">
                                    <div className="col-span-1 flex items-center pb-2">
                                        <label className="text-sm font-semibold text-gray-700">
                                            Profilo Giustificativi
                                        </label>
                                    </div>

                                    <div className="col-span-2 flex items-center gap-2 pb-2">
                                        {contract.justification_profile ? (
                                            <>
                                                <span className="text-sm font-semibold uppercase text-indigo-700">
                                                    {contract.justification_profile.name}
                                                </span>
                                            </>
                                        ) : (
                                            <span className="text-sm font-normal text-transparent">-</span>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                ))}
        </div>
    </>;
};

